import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import OverviewPage from './pages/OverviewPage';
import DashboardPage from './pages/DashboardPage';
import DerivedManualInputsPage from './pages/DerivedManualInputsPage';
import EntriesPage from './pages/EntriesPage';
import GradedSlipsPage from './pages/GradedSlipsPage';
import MarketsLayout from './pages/MarketsLayout';
import MarketsSummaryPage from './pages/MarketsSummaryPage';
import SleeperMarketsPage from './pages/SleeperMarketsPage';
import GameView from './pages/GameView/GameView';
import DoneFixturesPage from './pages/DoneFixturesPage';
import ActiveFixturesPage from './pages/ActiveFixturesPage';
import StatusPage from './pages/StatusPage';
import ManualInterventionsPage from './pages/ManualInterventionsPage';
import ConfigUpdatesPage from './pages/ConfigUpdatesPage';
import LimitsConfigPage from './pages/LimitsConfigPage';
import LimitsTablePage from './pages/LimitsTablePage';
import ObRungClearsPage from './pages/ObRungClearsPage';
import BatchSuspensionsPage from './pages/BatchSuspensionsPage';
import RiskRankPage from './pages/RiskRankPage';
import BatchCompetitorMutesPage from './pages/BatchCompetitorMutesPage';
import ModelMarketsPage from './pages/ModelMarketsPage';
import NbaDistributionsPage from './pages/NbaDistributionsPage';
import LineupsPage from './pages/LineupsPage';
import LiveFixturesPage from './pages/LiveFixturesPage';
import LiveDashboardPage from './pages/LiveDashboardPage';
import GameViewLayout from './pages/GameViewLayout';
import { AuthProvider, useAuth } from './auth/AuthContext';
import LoginPage from './auth/LoginPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

function MarketDashboardRedirect() {
  const location = useLocation();
  return <Navigate to={`/dashboard${location.search || ''}`} replace />;
}

function AppContent() {
  const { user, signOut } = useAuth();

  return (
    <Router>
      <div className="App">
        <nav className="navbar">
          <div className="nav-container">
            <Link to="/" className="nav-logo">
              <img
                src="/sleeper.jpg"
                alt="Sleeper"
                className="nav-logo-icon"
              />
              <span className="nav-logo-text">Risk Homebase</span>
            </Link>
            <div className="nav-links">
              <Link to="/" className="nav-link">Overview</Link>
              <Link to="/dashboard" className="nav-link">Dashboard</Link>
              <div className="nav-dropdown">
                <span className="nav-link nav-dropdown-trigger" tabIndex={0} role="button" aria-haspopup="true">
                  Game View
                </span>
                <div className="nav-dropdown-menu" role="menu">
                  <NavLink
                    to="/game/summary"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Summary
                  </NavLink>
                  <NavLink
                    to="/game/live"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Live
                  </NavLink>
                  <NavLink
                    to="/game/active"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Active
                  </NavLink>
                </div>
              </div>
              <div className="nav-dropdown">
                <span className="nav-link nav-dropdown-trigger" tabIndex={0} role="button" aria-haspopup="true">
                  Markets
                </span>
                <div className="nav-dropdown-menu" role="menu">
                  <NavLink
                    to="/markets/summary"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Summary
                  </NavLink>
                  <NavLink
                    to="/markets/models"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Sleeper models
                  </NavLink>
                  <NavLink to="/markets/nba-distributions" className="nav-dropdown-item" role="menuitem">NBA distributions</NavLink>
                </div>
              </div>
              <div className="nav-dropdown">
                <span className="nav-link nav-dropdown-trigger" tabIndex={0} role="button" aria-haspopup="true">
                  Entries
                </span>
                <div className="nav-dropdown-menu" role="menu">
                  <NavLink
                    to="/entries/ticker"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Ticker
                  </NavLink>
                  <NavLink
                    to="/entries/exposure"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Exposure / aggregate
                  </NavLink>
                  <NavLink
                    to="/entries/replication"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Replication
                  </NavLink>
                  <NavLink
                    to="/entries/graded"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Graded slips
                  </NavLink>
                </div>
              </div>
              <Link to="/done" className="nav-link">Done</Link>
              <div className="nav-dropdown">
                <span className="nav-link nav-dropdown-trigger" tabIndex={0} role="button" aria-haspopup="true">
                  Operations
                </span>
                <div className="nav-dropdown-menu" role="menu">
                  <Link to="/manual/summary" className="nav-dropdown-item" role="menuitem">
                    Summary
                  </Link>
                  <Link to="/manual/config-updates" className="nav-dropdown-item" role="menuitem">
                    Config updates
                  </Link>
                  <Link to="/manual/limits" className="nav-dropdown-item" role="menuitem">
                    Limits
                  </Link>
                  <Link to="/manual/alerts" className="nav-dropdown-item" role="menuitem">
                    OB ladder clears
                  </Link>
                  <Link to="/manual/batch-suspensions" className="nav-dropdown-item" role="menuitem">
                    Batch suspension
                  </Link>
                  <Link to="/manual/risk-rank" className="nav-dropdown-item" role="menuitem">
                    Risk rank
                  </Link>
                  <Link to="/manual/batch-competitor-mutes" className="nav-dropdown-item" role="menuitem">
                    Batch competitor mute
                  </Link>
                  <Link to="/manual/lineups" className="nav-dropdown-item" role="menuitem">
                    Lineups
                  </Link>
                  <Link to="/manual/model-markets" className="nav-dropdown-item" role="menuitem">
                    Model markets
                  </Link>
                  <Link to="/manual/derived-inputs" className="nav-dropdown-item" role="menuitem">
                    Derived manual inputs
                  </Link>
                  <NavLink
                    to="/status"
                    className={({ isActive }) =>
                      `nav-dropdown-item${isActive ? ' nav-dropdown-item-active' : ''}`
                    }
                    role="menuitem"
                  >
                    Status
                  </NavLink>
                </div>
              </div>
            </div>
            {user && (
              <div className="nav-user">
                {user.picture && (
                  <img src={user.picture} alt="" className="nav-user-avatar" />
                )}
                <span className="nav-user-email">{user.email}</span>
                <button type="button" onClick={signOut} className="nav-signout-btn">Sign out</button>
              </div>
            )}
          </div>
        </nav>
        <main className="main-content">
          <Routes>
            <Route path="/" element={<OverviewPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/game">
              <Route element={<GameViewLayout />}>
                <Route index element={<Navigate to="summary" replace />} />
                <Route path="summary" element={<ActiveFixturesPage pageTitle="Summary" />} />
                <Route path="live" element={<LiveFixturesPage />} />
                <Route path="active" element={<ActiveFixturesPage pageTitle="Active Fixtures" />} />
              </Route>
              <Route path=":fixtureId" element={<GameView />} />
            </Route>
            <Route path="/active" element={<Navigate to="/game/active" replace />} />
            <Route path="/market-dashboard" element={<MarketDashboardRedirect />} />
            <Route path="/market" element={<MarketDashboardRedirect />} />
            <Route path="/entries" element={<Navigate to="/entries/ticker" replace />} />
            <Route path="/entries/graded" element={<GradedSlipsPage />} />
            <Route path="/entries/:tab" element={<EntriesPage />} />
            <Route path="/markets" element={<MarketsLayout />}>
              <Route index element={<Navigate to="/markets/summary" replace />} />
              <Route path="summary" element={<MarketsSummaryPage />} />
              <Route path="models" element={<SleeperMarketsPage />} />
              <Route path="nba-distributions" element={<NbaDistributionsPage />} />
              <Route path="ticker" element={<Navigate to="/markets/models" replace />} />
              <Route path="exposure" element={<Navigate to="/markets/models" replace />} />
            </Route>
            <Route path="/derived" element={<Navigate to="/markets/models" replace />} />
            <Route path="/derived/models" element={<Navigate to="/markets/models" replace />} />
            <Route path="/derived/ticker" element={<Navigate to="/markets/models" replace />} />
            <Route path="/derived/exposure" element={<Navigate to="/markets/models" replace />} />
            <Route path="/derived/:tab" element={<Navigate to="/markets/models" replace />} />
            <Route path="/live" element={<Navigate to="/game/live" replace />} />
            <Route path="/live/:fixtureId" element={<LiveDashboardPage />} />
            <Route path="/done" element={<DoneFixturesPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/manual" element={<Navigate to="/manual/summary" replace />} />
            <Route path="/manual/summary" element={<ManualInterventionsPage />} />
            <Route path="/manual/config-updates" element={<ConfigUpdatesPage />} />
            <Route path="/manual/limits" element={<LimitsConfigPage />} />
            <Route path="/manual/limits/table" element={<LimitsTablePage />} />
            <Route path="/manual/alerts" element={<ObRungClearsPage />} />
            <Route path="/manual/batch-suspensions" element={<BatchSuspensionsPage />} />
            <Route path="/manual/risk-rank" element={<RiskRankPage />} />
            <Route path="/manual/batch-competitor-mutes" element={<BatchCompetitorMutesPage />} />
            <Route path="/manual/lineups" element={<LineupsPage />} />
            <Route path="/manual/model-markets" element={<ModelMarketsPage />} />
            <Route path="/manual/derived-inputs" element={<DerivedManualInputsPage />} />
            <Route path="/batch-suspensions" element={<Navigate to="/manual/batch-suspensions" replace />} />
            <Route path="/batch-competitor-mutes" element={<Navigate to="/manual/batch-competitor-mutes" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

function AuthGate() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#1a1a2e', color: '#e0e0e0' }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return <AppContent />;
}

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <AuthGate />
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
